import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { BaseDialogComponent } from 'packages/client/src/app/components/kit/base-dialog/base-dialog.component';
import { SelectInputComponent } from 'packages/client/src/app/components/kit/input/select-input/select-input.component';
import { NumberInputComponent } from 'packages/client/src/app/components/kit/input/text-input/number-input.component';
import { DateInputComponent } from 'packages/client/src/app/components/kit/input/date-input/date-input.component';
import { TextAreaInputComponent } from 'packages/client/src/app/components/kit/input/text-area-input/text-area-input.component';
import { fb } from 'packages/client/src/app/modules/shared/shared.global-service';
import { isInvalid } from 'packages/client/src/app/modules/shared/helpers/validate';
import { WarehouseMaterialOperationDialogStore } from './warehouse-material-operation-dialog.store';
import { WarehouseMaterialOperationTypeEnum } from '@libs/types/src/services/enum/warehouse-material-operation.enum';
import { enumToOptions } from '@libs/types/src/common/enum/util';
import { contentGlobalSuccess } from '../../../../content.error';
import { WarehouseMaterialOperationObject } from '@libs/types/src/services/object-type/service/social-service/warehouse-material-operation.object';
import { getWarehouseMaterialOperationsFrag } from '../warehouse-material-operation-table/warehouse-material-operation-table.query';
import { InputUserMultiselectComponent } from "packages/client/src/app/modules/shared/components/inputs/input-user-multiselect/input-user-multiselect.component";
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import { InfiniteScrollStore } from 'packages/client/src/app/modules/shared/store/common/infinite-scroll.store';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { FormatPersonPipe } from 'packages/client/src/app/modules/shared/pipes/format-person.pipe';
import { folderFrag } from 'packages/client/src/app/services/types/folder.types';

@Component({
    selector: 'app-warehouse-material-operation-dialog',
    templateUrl: 'warehouse-material-operation-dialog.component.html',
    providers: [WarehouseMaterialOperationDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    SelectInputComponent,
    NumberInputComponent,
    DateInputComponent,
    TextAreaInputComponent,
    InputUserMultiselectComponent,
        FormatPersonPipe,
],
})
export class WarehouseMaterialOperationDialogComponent extends BaseDialogComponent {

    protected readonly store = inject(WarehouseMaterialOperationDialogStore);
    protected readonly prefix = 'service.warehouseMaterialOperation.';
    protected readonly typeOptions = enumToOptions(WarehouseMaterialOperationTypeEnum, this.prefix + 'type.');
    protected readonly EmploymentParentGroupEnum = EmploymentParentGroupEnum;
    protected readonly WarehouseMaterialOperationTypeEnum = WarehouseMaterialOperationTypeEnum;
    protected readonly serviceId = computed(() => this.dialogConfig.data.serviceId);

    protected foldersScroll = computed(() => new InfiniteScrollStore(
        this.store.api.getFolders,
        FilterBuilder.singlePage({ serviceId: this.serviceId() || 0 }),
        true,
        folderFrag,
        undefined,
        true,
    ),
    );

    protected form = fb.group({
        id: fb.control<number | null>(null),
        type: fb.control<string | null>(null, Validators.required),
        amount: fb.control<number>(1, Validators.required),
        date: fb.control<Date | null>(new Date()),
        description: fb.control<string | null>(null),
        workerIds: fb.control<string[]>([]),
        folderId: fb.control<number | null>(null),
    });

    private materialId!: number;
    private item: WarehouseMaterialOperationObject | null = null;

    constructor() {
        super();
        this.materialId = this.dialogConfig.data.materialId;
        this.item = this.dialogConfig.data.item ?? null;

        if (this.item) {
            this.form.patchValue({
                id: this.item.id,
                type: this.item.type,
                amount: this.item.amount,
                date: this.item.date ? new Date(this.item.date) : null,
                description: this.item.description,
                workerIds: this.item.workerIds,
                folderId: this.item.folderId,
            });
        }
    }

    override save(_event: Event): void {
        if (isInvalid(this.form)) return;

        const data = {
            type: this.form.controls.type.value! as WarehouseMaterialOperationTypeEnum,
            amount: this.form.controls.amount.value!,
            date: this.form.controls.date.value?.toISOString() ?? null,
            description: this.form.controls.description.value || null,
            folderId: this.form.controls.folderId.value || null,
            workerIds: this.form.controls.workerIds.value || [],
            warehouseMaterialId: this.materialId,
        };

        if (data.type === WarehouseMaterialOperationTypeEnum.RECEIPT) {
            data.folderId = null
        }

        if (this.item) {
            this.store.api.patchWarehouseMaterialOperation({
                id: this.item.id,
                ...data,
            }, getWarehouseMaterialOperationsFrag).then((res) => {
                if (!res) return

                contentGlobalSuccess.next('common.updated');
                this.dialogRef.close({ refresh: true });
            });
        } else {
            this.store.api.createWarehouseMaterialOperation(data, getWarehouseMaterialOperationsFrag).then((res) => {
                if (!res) return

                contentGlobalSuccess.next('common.created');
                this.dialogRef.close({ refresh: true });
            });
        }
    }
}
